import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * AuthCallback — one-shot exchange of the `#session_id=...` fragment that
 * Emergent-managed Google Auth returns to the main app route.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The redirect URL is derived from `window.location.origin`
 * in AuthContext.login() so this screen keeps working across preview, custom
 * domains, and local dev.
 *
 * This component is rendered by AppRouter (not a <Route>) the moment
 * `useLocation().hash` contains `session_id=`. Reading the hash via
 * `useLocation()` keeps the detection reactive — once we `replaceState`
 * away the fragment, React re-renders and we fall back to normal routing.
 */
const AuthCallback = () => {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ran = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Guard against StrictMode's double-invoke of effects — the session_id
    // from Emergent is single-use, so a second exchange returns 404.
    if (ran.current) return;
    ran.current = true;
    const hash = location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("Missing session_id");
      return;
    }
    (async () => {
      try {
        // Pull any referral code captured earlier by the landing page.
        let refCode = null;
        try {
          refCode = window.localStorage.getItem("quill_ref_code");
        } catch { /* ignore */ }
        await exchangeSession(sessionId, refCode);
        try { window.localStorage.removeItem("quill_ref_code"); } catch { /* ignore */ }
        // Strip the fragment before navigating so a hard-refresh doesn't
        // retry an already-consumed session_id.
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Login failed");
      }
    })();
  }, [exchangeSession, navigate, location.hash]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white" data-testid="auth-callback">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Quill</div>
        <div className="font-display text-2xl mt-3 tracking-tight">
          {error ? "Sign-in failed" : "Signing you in…"}
        </div>
        {error && (
          <>
            <div className="text-rose-400 text-sm mt-4" data-testid="auth-error">{error}</div>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="mt-6 text-[11px] uppercase tracking-[0.2em] text-zinc-400 underline underline-offset-4 hover:text-white"
              data-testid="auth-error-retry"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
